import { isoDateTime, z } from "../zod.js";
import { Uuid } from "../identity/admin-schemas.js";
import { NUL_ISSUE, textHasNoNul } from "../common/text.js";

// Module 09, the CUSTOMER RECORD's contract DTOs (spec 09 §2.26 to §2.28 and
// §5's P3-E2 PR E2-2a1 rows; D-323 rulings 1 to 5): a customer's notes
// timeline with its one pinned note, the tenant's tag list, and the tags a
// customer carries. Since P3-E2 PR E2-2c (spec 09 §3.13, §5's E2-2 row 14;
// D-324 rulings 1 to 3) it also holds the duplicate check's request and
// answer, and since P3-E2 PR E2-2a2 (spec 09 §2.29, §5's E2-2 rows 10 to 13,
// §7 narrowing 19; D-323 rulings 8 to 12, D-335) the sales attachments, both
// at the foot of the file. Routes live in `./crm-register.ts`. The customer book's
// and the 360's new fields (`tags`, `has_live_business`, `pinned_note`) are
// declared on their own schemas in `./schemas.ts`, which imports the shapes
// below, so this file imports nothing from `./schemas.ts` and stays a leaf.
//
// WHAT THIS FILE HOLDS, AND THE RULES IT RESTATES BECAUSE THEY BITE HERE:
//  * A NOTE IS WORDS AND NEVER MONEY. Its body is served to every reader of
//    the customer (spec 09 §7's redaction table, D-323 ruling 3), and no
//    agent tool serves it (§8). An audit row carries its length and never
//    its words (§7's payload table).
//  * A REDACTED NOTE keeps its author, its time and its pin, and its body is
//    the fixed marker `[redacted]` with `redacted_at` and `redacted_by_name`
//    set. The redaction door is built on the recommendation of spec 09 §12's
//    note-redaction question (D-323 ruling 3, owner-pending).
//  * A TAG NAME is the tenant's own word, 1 to 40 characters after trim and
//    never holding `;`, because the book's export joins a customer's names
//    with `; ` and the import lane splits on `;` (§2.27). A tag has no colour
//    and renders as the neutral chip (D-323 ruling 5).
//  * `in_use` IS A BOOLEAN, never a count, so a reader the customer door
//    narrows learns nothing about the size of the book (§5's items).
//  * Lists answer `{items, next_cursor}` envelopes, cursor keyset, `limit`
//    1..200, default 50.

/** `typed` for a note a person wrote, `legacy` for one migration 0077 or 0078
 * copied from `customers.notes` (§2.26). A legacy note has no known author,
 * and the screen shows it as carried over. */
export const CustomerNoteOrigin = z.enum(["typed", "legacy"]).openapi("CustomerNoteOrigin");

/** One note on a customer's timeline (spec 09 §2.26, §5's E2-2 items). */
export const CustomerNote = z
  .object({
    id: Uuid,
    customer_id: Uuid,
    body: z.string().openapi({
      description:
        "The note as typed, or the fixed marker `[redacted]` once an admin has redacted it (D-323 ruling 3). Served to every reader of the customer.",
    }),
    pinned: z.boolean(),
    pinned_at: isoDateTime().nullable(),
    author_user_id: Uuid.nullable().openapi({
      description:
        "The writer of a `typed` note. Null on a `legacy` note, whose author is unknown.",
    }),
    author_name: z.string().nullable(),
    origin: CustomerNoteOrigin,
    redacted_at: isoDateTime().nullable(),
    redacted_by_name: z.string().nullable(),
    created_at: isoDateTime(),
  })
  .openapi("CustomerNote");

/** The note as its four write doors answer it (append, pin, unpin, redact).
 * Spread rather than extended, so it publishes as one flat object (the
 * `CustomerWithNextStep` reason in `./schemas.ts`). `body` is optional here
 * alone, because the replay store keeps no note text (D-339). */
export const CustomerNoteWriteResponse = z
  .object({
    ...CustomerNote.shape,
    body: CustomerNote.shape.body.optional().openapi({
      description:
        "The note as typed, or the fixed marker `[redacted]` once an admin has redacted it (D-323 ruling 3). Absent on an idempotent replay, because the replay store keeps no note text (spec 09 §2.26, D-339); the client reads the timeline again.",
    }),
  })
  .openapi("CustomerNoteWriteResponse");

export const CustomerNoteListResponse = z
  .object({ items: z.array(CustomerNote), next_cursor: z.string().nullable() })
  .openapi("CustomerNoteListResponse");

/**
 * `POST /v1/customers/{id}/notes` (spec 09 §5's E2-2 row 2). The body is
 * stored as typed and must hold something other than whitespace. It is at most
 * 4000 Unicode code points as sent, the cap the customer's `notes` field has
 * (zod 4 measures a string's length in code points, D-643), so a pinned note
 * always round-trips through PATCH `notes`; that bound keeps it inside
 * §2.26's CHECK, which counts code points after trim.
 * `pin: true` pins the new note and unpins the pinned one in the same
 * transaction.
 */
export const CustomerNoteCreateRequest = z
  .object({
    body: z
      .string()
      .min(1)
      .max(4000)
      .refine((value) => value.trim().length > 0, {
        message: "a note holds something other than whitespace",
        params: { reason: "note_body_blank" },
      })
      // Postgres text cannot store U+0000, so a body holding one would pass
      // here and end as a database 500 (`../common/text.ts`).
      .refine(textHasNoNul, NUL_ISSUE),
    pin: z.boolean().optional(),
  })
  .strict()
  .openapi("CustomerNoteCreateRequest");

/** `active` or `archived` (§2.27). Archiving hides a tag from every picker and
 * keeps its links, so an archived tag still shows on the customers that carry
 * it and still filters. */
export const SalesTagStatus = z.enum(["active", "archived"]).openapi("SalesTagStatus");

/** A tag the customer carries, as the list items, the 360 and rows 8 and 9
 * serve it: ordered by the lower-cased name and then the id, with its status
 * so an archived tag renders as such (§5's E2-2 items). */
export const CustomerTagRef = z
  .object({ id: Uuid, name: z.string(), status: SalesTagStatus })
  .openapi("CustomerTagRef");

/** One of the tenant's tags (spec 09 §2.27, §5's E2-2 items). */
export const SalesTag = z
  .object({
    id: Uuid,
    name: z.string(),
    status: SalesTagStatus,
    in_use: z.boolean().openapi({
      description:
        "True while an ACTIVE customer carries the tag. A boolean and never a count, so a reader the customer door narrows learns nothing about the size of the book. An archived tag that only archived customers carry reads false and leaves the Tag filters, and a saved view naming it still filters (spec 09 §9).",
    }),
    created_at: isoDateTime(),
  })
  .openapi("SalesTag");

export const SalesTagListResponse = z
  .object({ items: z.array(SalesTag), next_cursor: z.string().nullable() })
  .openapi("SalesTagListResponse");

/** Which archived tags join the active ones on `GET /v1/sales-tags`: `none`
 * (the default), `in_use` (those an active customer carries, which the Tag
 * filters read) or `all` (§4.13's Show archived). */
export const SalesTagArchivedFilter = z
  .enum(["none", "in_use", "all"])
  .openapi("SalesTagArchivedFilter");

/** A tag name as a write sends it: stored with its leading and trailing
 * whitespace trimmed (`String.prototype.trim`), 1 to 40 Unicode code points
 * after that trim, the count the table's `char_length` CHECK makes, never
 * holding `;` (spec 09 §2.27) and never holding U+0000, which Postgres text
 * cannot store. */
const SalesTagName = z
  .string()
  .max(200)
  .regex(/^[^;]*$/u, "a tag name never holds ';'")
  .refine(textHasNoNul, NUL_ISSUE)
  .refine(
    (value) => {
      const trimmed = Array.from(value.trim()).length;
      return trimmed >= 1 && trimmed <= 40;
    },
    {
      message: "a tag name is 1 to 40 characters after trim",
      params: { reason: "tag_name_length" },
    },
  )
  .openapi({
    description:
      "Stored with its leading and trailing whitespace trimmed. 1 to 40 Unicode code points after that trim, at most 200 code points as sent, and never holding `;`, which the book's export joins names with and the import lane splits on (spec 09 §2.27).",
  });

/** `POST /v1/sales-tags` (spec 09 §5's E2-2 row 5). */
export const SalesTagCreateRequest = z
  .object({ name: SalesTagName })
  .strict()
  .openapi("SalesTagCreateRequest");

/** `PATCH /v1/sales-tags/{id}`, the rename (spec 09 §5's E2-2 row 6). */
export const SalesTagUpdateRequest = z
  .object({ name: SalesTagName })
  .strict()
  .openapi("SalesTagUpdateRequest");

/** `POST /v1/customers/{id}/tags` (spec 09 §5's E2-2 row 8). */
export const CustomerTagAttachRequest = z
  .object({ tag_id: Uuid })
  .strict()
  .openapi("CustomerTagAttachRequest");

/** The answer of the tag and untag doors: every tag the customer carries
 * after the write (spec 09 §5's E2-2 rows 8 and 9). */
export const CustomerTagsResponse = z
  .object({ tags: z.array(CustomerTagRef) })
  .openapi("CustomerTagsResponse");

// ===========================================================================
// The duplicate check (P3-E2 PR E2-2c; spec 09 §3.13's first two paragraphs,
// §5's E2-2 row 14, §7 narrowing 20; D-324 rulings 1 to 3)
// ===========================================================================
//
// A WARNING AND NEVER A BLOCK. The check is a dry run the New customer form
// calls after a pause in typing; the create itself answers 201 whatever it
// finds and names `POSSIBLE_DUPLICATE` in `X-Sales-Warning` (§5's E2-2 row 18).
//
// WHAT A CANDIDATE IS. An active customer with the same `tax_id` first, then
// customers whose `display_name` or `legal_name` is similar through the
// trigram operator, re-scored with every market's legal-form words removed
// (`packages/domain-kernel/src/company-name/legal-forms.ts`) and kept at a
// similarity of 0.5 or more. The 0.5 is PROVISIONAL until D-324's census of
// staging's customer names fixes it (D-332 ruling 4), so the contract states
// the range of `score` and never the threshold. Archived customers are never
// candidates. Five at most, the exact tax-id match first.
//
// WHO SEES WHAT (§7 narrowing 20). A candidate is a customer the caller's
// customer door answers, and its legal name reaches the caller only as far as
// `LegalNameReach` serves it, so `legal_name` is optional. For a caller the
// door narrows to assigned customers, `hidden_count` counts only DISPLAY-NAME
// matches on customers it cannot open, because every reader is served a
// customer's display name (#272). A legal-name or tax-id match on such a
// customer gives no signal at all, since either would tell a representative
// that a given company is the factory's customer.

/** ISO 3166-1 alpha-2, the shape `SalesCountry` in `./schemas.ts` has. Restated
 * here because this file imports nothing from `./schemas.ts` (the header). */
const DuplicateCheckCountry = z
  .string()
  .regex(/^[A-Z]{2}$/)
  .openapi({ example: "KZ", description: "ISO 3166-1 alpha-2 (char(2))" });

/**
 * `POST /v1/customers/check-duplicates` (spec 09 §5's E2-2 row 14). Every
 * field is optional, because the form checks whatever the user has typed so
 * far, and each carries the bound the create form's field has
 * (`CustomerCreateRequest`), so a value the form can hold is a value the check
 * accepts. A body that names no legal name, no display name and no tax id (or
 * names only whitespace) answers no candidates and `hidden_count` 0.
 * `exclude_customer_id` leaves one customer out of the answer, so a check run
 * from an existing customer's own form does not name that customer.
 */
export const CustomerDuplicateCheckRequest = z
  .object({
    legal_name: z.string().max(300).refine(textHasNoNul, NUL_ISSUE).optional(),
    display_name: z.string().max(200).refine(textHasNoNul, NUL_ISSUE).optional(),
    tax_id: z.string().max(40).refine(textHasNoNul, NUL_ISSUE).optional(),
    country: DuplicateCheckCountry.optional(),
    exclude_customer_id: Uuid.optional(),
  })
  .strict()
  .openapi("CustomerDuplicateCheckRequest");

/** Which field made the candidate: `tax_id` for an exact tax-id match among
 * active customers, which ranks first whatever the names say, and otherwise
 * the name, `display_name` or `legal_name`, whose similarity scored higher
 * (spec 09 §3.13). A `legal_name` match is served only to a caller whose
 * `LegalNameReach` serves that customer's legal name. */
export const CustomerDuplicateMatch = z
  .enum(["tax_id", "display_name", "legal_name"])
  .openapi("CustomerDuplicateMatch");

/** One customer the check names (spec 09 §5's E2-2 row 14). */
export const CustomerDuplicateCandidate = z
  .object({
    id: Uuid,
    customer_no: z.string(),
    display_name: z.string(),
    legal_name: z.string().optional().openapi({
      description:
        "Served only as far as the caller's `LegalNameReach` serves this customer's legal name (spec 09 §3.13, #272); absent otherwise.",
    }),
    match: CustomerDuplicateMatch,
    score: z.number().min(0).max(1).openapi({
      description:
        "The trigram similarity after legal-form words are removed, 0 to 1, and 1 for an exact tax-id match. Candidates are ordered by it after the tax-id match.",
    }),
  })
  .openapi("CustomerDuplicateCandidate");

/** The check's answer: at most five candidates, the exact tax-id match first,
 * and the count of display-name matches on customers the caller cannot open
 * (§7 narrowing 20). `hidden_count` is 0 for a caller the customer door does
 * not narrow, since every match it has is a candidate it can open. */
export const CustomerDuplicateCheckResponse = z
  .object({
    candidates: z.array(CustomerDuplicateCandidate).max(5),
    hidden_count: z.number().int().min(0).openapi({
      description:
        "Display-name matches on active customers the caller's customer door does not answer. A legal-name or tax-id match on such a customer is never counted, because it would tell the caller that the company is the factory's customer (spec 09 §3.13, §7 narrowing 20).",
    }),
  })
  .openapi("CustomerDuplicateCheckResponse");

// ===========================================================================
// Sales attachments (P3-E2 PR E2-2a2; spec 09 §2.29, §5's E2-2 rows 10 to 13,
// §7 narrowings 19, 21 and 22; D-323 rulings 8 to 12, D-335)
// ===========================================================================
//
// ONE FILE, ONE LINK, ONE SUBJECT. The file itself lives in the platform file
// registry (D-064) under the purpose `sales_attachment`, uploaded by the
// browser through the registry's three steps (`POST /v1/files`, the PUT, then
// `POST /v1/files/{id}/store`, which checks the bytes against the declared
// type and refuses a mismatch with `FILE_INTEGRITY_MISMATCH`). The attach then
// writes module 09's link to exactly one of a customer, a quote or an order
// (`sales_attachments_one_subject`), and a file has one link for good, so a
// detached file is never attached again (`ATTACHMENT_ALREADY_LINKED`).
//
// THE CATEGORY FENCE (§7 narrowing 19, D-335 ruling 1, which answered spec 09
// §12 question 8 with its default). A `technical` file is listed and
// downloaded by every reader of its subject, qc and the storekeeper on the
// orders and quotes they read included, because drawings and certificates
// serve inspection and packing. A `commercial` file is listed, downloaded and
// detached only by a holder of `sales.read_value`. Any other reader is served
// `withheld_count` in its place, and its download and detach answer 404
// `ATTACHMENT_NOT_FOUND` as an unknown id does. The category is chosen at
// upload and is required on the body. The upload form starts on
// `commercial`, and on `technical` for a DWG drawing (D-335 ruling 2).
//
// NO WORDS IN THE TRAIL. The audit rows carry the file id, the subject and the
// category and never the title (§7's E2-2 payload table), and no agent tool
// serves an attachment (§8).

/** `technical` (drawings, certificates, every reader of the subject) or
 * `commercial` (contracts and price agreements, `sales.read_value` alone),
 * spec 09 §2.29 and §7 narrowing 19. */
export const SalesAttachmentCategory = z
  .enum(["technical", "commercial"])
  .openapi("SalesAttachmentCategory");

/** `active`, or `detached`, which keeps the link row and the file (§2.29). A
 * detached attachment is listed only with `include_detached=true`. */
export const SalesAttachmentStatus = z
  .enum(["active", "detached"])
  .openapi("SalesAttachmentStatus");

/** The subject an attachment hangs off, named after the body's and the
 * query's id keys without `_id`: `customer` (`customer_id`), `quote`
 * (`quote_id`) or `sales_order` (`sales_order_id`). */
export const SalesAttachmentSubjectKind = z
  .enum(["customer", "quote", "sales_order"])
  .openapi("SalesAttachmentSubjectKind");

/** The subject keys, in the order the body and the query name them. Exactly
 * one is sent (spec 09 §5's E2-2 rows 10 and 11). */
export const salesAttachmentSubjectKeys = ["customer_id", "quote_id", "sales_order_id"] as const;

/** One attachment as every attachment door serves it (spec 09 §5's E2-2
 * items). `content_type` and `byte_size` are the registry's verified values,
 * read from the stored file, never the uploader's declaration. */
export const SalesAttachment = z
  .object({
    id: Uuid,
    file_id: Uuid,
    subject_kind: SalesAttachmentSubjectKind,
    subject_id: Uuid.openapi({
      description:
        "The customer, quote or order the file hangs off, as `subject_kind` names it. A customer attachment a merge moved names the winner (spec 09 §3.13).",
    }),
    category: SalesAttachmentCategory,
    title: z.string().nullable().openapi({
      description:
        "The label the uploader typed, at most 200 characters, or null when none was given (D-229: tenant-local, unpoolable). Never written to the audit trail.",
    }),
    content_type: z.string().openapi({
      description:
        "The type the registry verified from the stored bytes at `store()` (spec 09 §2.29), one of the types the `sales_attachment` purpose admits. The download is named with this type's one extension.",
    }),
    byte_size: z.number().int().min(0),
    status: SalesAttachmentStatus,
    attached_by_name: z.string().nullable().openapi({
      description:
        "The name of the member who attached the file, null when the user record carries none.",
    }),
    attached_at: isoDateTime(),
    detached_at: isoDateTime().nullable(),
  })
  .openapi("SalesAttachment");

/** The attachment as the attach and the detach answer it. `title` is optional
 * here alone: the replay store keeps no title, because a replay sits outside
 * §7 narrowing 19's fence on a commercial file's label (D-339). */
export const SalesAttachmentWriteResponse = z
  .object({
    ...SalesAttachment.shape,
    title: SalesAttachment.shape.title.optional().openapi({
      description:
        "The label the uploader typed, at most 200 characters, or null when none was given (D-229: tenant-local, unpoolable). Never written to the audit trail. Absent on an idempotent replay, because the replay store keeps no title (spec 09 §2.29, D-339); the client reads the list again.",
    }),
  })
  .openapi("SalesAttachmentWriteResponse");

/** A title as the attach sends it: stored with its leading and trailing
 * whitespace trimmed, and stored as null when nothing is left. At most 200
 * Unicode code points after that trim, the count `char_length` makes in the
 * table's CHECK, at most 1000 code points as sent, and never U+0000, which
 * Postgres text cannot store. */
const SalesAttachmentTitle = z
  .string()
  .max(1000)
  .refine(textHasNoNul, NUL_ISSUE)
  .refine((value) => Array.from(value.trim()).length <= 200, {
    message: "a title is at most 200 characters after trim",
    params: { reason: "attachment_title_length" },
  })
  .openapi({
    description:
      "Optional. Stored with its leading and trailing whitespace trimmed, and as null when nothing is left. At most 200 Unicode code points after that trim (spec 09 §2.29).",
  });

/**
 * `POST /v1/sales-attachments` (spec 09 §5's E2-2 row 10). Names a stored
 * registry file of the purpose `sales_attachment` and exactly one subject. A
 * body that names none, or more than one, is the door's first refusal, 400
 * `VALIDATION_FAILED` with the reason `attachment_one_subject`, before the
 * service reads anything.
 */
export const SalesAttachmentCreateRequest = z
  .object({
    file_id: Uuid,
    customer_id: Uuid.optional(),
    quote_id: Uuid.optional(),
    sales_order_id: Uuid.optional(),
    category: SalesAttachmentCategory,
    title: SalesAttachmentTitle.nullable().optional(),
  })
  .strict()
  .superRefine((body, issue) => {
    const named = salesAttachmentSubjectKeys.filter((key) => body[key] !== undefined);
    if (named.length !== 1) {
      issue.addIssue({
        code: "custom",
        path: named.slice(1, 2),
        message: "an attachment names exactly one of customer_id, quote_id and sales_order_id",
        params: { reason: "attachment_one_subject" },
      });
    }
  })
  .openapi("SalesAttachmentCreateRequest");

/**
 * `GET /v1/sales-attachments` (spec 09 §5's E2-2 row 11). `items` holds the
 * attachments the caller may read, newest first. `withheld_count` counts the
 * subject's commercial attachments the list left out for a caller without
 * `sales.read_value`, over the whole subject and the statuses the read asked
 * for, never one page of it, and is 0 for a holder (§7 narrowing 19).
 */
export const SalesAttachmentListResponse = z
  .object({
    items: z.array(SalesAttachment),
    withheld_count: z.number().int().min(0).openapi({
      description:
        "How many of the subject's `commercial` attachments the caller is not shown, counted over the whole subject and the statuses the read asked for (active, or every status with `include_detached=true`), never one page of it. 0 for a holder of `sales.read_value` (spec 09 §7 narrowing 19).",
    }),
    next_cursor: z.string().nullable(),
  })
  .openapi("SalesAttachmentListResponse");

/** `GET /v1/sales-attachments/{id}/download` (spec 09 §5's E2-2 row 12): the
 * registry's presigned GET for five minutes, served with `attachment`
 * disposition and named from the verified type (§2.29). */
export const SalesAttachmentDownloadResponse = z
  .object({
    url: z.url().openapi({
      description:
        "A presigned GET minted per request from the registry row's key, never from a caller-supplied key. Served with `Content-Disposition: attachment`, named from the sanitised stem with every dot replaced plus the one extension of the verified type, with an RFC 6266 `filename*` UTF-8 form so a Cyrillic name survives.",
    }),
    expires_at: isoDateTime().openapi({
      description: "Five minutes after the URL was minted.",
    }),
  })
  .openapi("SalesAttachmentDownloadResponse");
